import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    model,
    output,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
    DataManagerColunaCriterio,
    DataManagerColunaGrupo,
    DataManagerColunaSimples,
    DataManagerController,
    DataManagerProxy,
} from '@shared/infra/data-manager';
import { VexChartComponent } from '@vex/components/vex-chart/vex-chart.component';
import { ApexOptions } from 'apexcharts';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { CriterioTipoDado } from '@shared/criterio';
import { DashboardPreferenciaCardOrigemModel } from '../../../models/dashboard-config.model';
import {
    DashboardCardModel,
    DashboardCardTipo,
    DashboardDadoCardItemModel,
} from '../../../models/dashboard.model';
import { DashboardService } from '../../../services/dashboard.service';

@Component({
    selector: 'app-dashboard-card-chart',
    templateUrl: './dashboard-card-chart.component.html',
    styleUrl: './dashboard-card-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkDragHandle,
        MatCheckbox,
        MatIcon,
        MatIconButton,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        NgTemplateOutlet,
        VexChartComponent,
    ]
})
export class DashboardCardChartComponent {
  card = input<DashboardCardModel>();
  dmController =
    input.required<DataManagerController<DashboardDadoCardItemModel>>();
  fullScreenHabilitado = input(false);
  alternarFullscreen = output();
  origemDados = model<DashboardPreferenciaCardOrigemModel>();

  // Injectables
  private dashboardSvc = inject(DashboardService);

  // Private
  private labelDadoAdicional = computed(() => this.buildDadosAdicionais());
  private refreshNecessario = computed(
    () => this.dmController().refreshNecessario(),
    { equal: () => false }
  );

  // Protected
  protected options = computed(() => this.buildOptions());
  protected series = computed(() => this.buildSeries());
  protected readonly tipoCard = DashboardCardTipo;

  protected onOcultarCard() {
    this.dashboardSvc.ocultarCard(this.card()!.id);
  }

  // Métodos private
  private buildDadosAdicionais(): Map<string, string> {
    const result = new Map<string, string>();

    const itens = this.dmController().conteudoVisivelDados() as DataManagerProxy<DashboardDadoCardItemModel>[];
    const card = this.card();
    const colunas = this.dmController().colunas();
    const dimensao = card?.dimensoes[0];

    const colunaDimensao = colunas.find((col) => col.id === dimensao?.id) as
      | DataManagerColunaSimples
      | DataManagerColunaCriterio;
    if (!colunaDimensao) {
      return result;
    }

    itens.forEach((item) => {
      const label = item.get(colunaDimensao);
      let labelDadoAdicional = label;
      for (const colDadoAdicional of colunas) {
        if (
          colDadoAdicional.tipoColuna === 'dadoAdicional' &&
          colDadoAdicional.colunaMaster === colunaDimensao &&
          colDadoAdicional.sel
        ) {
          labelDadoAdicional += ` - ${item.get(colDadoAdicional)}`;
        }
      }
      result.set(label, labelDadoAdicional);
    });
    return result;
  }

  private buildOptions(): ApexOptions | null {
    const refreshNecessario = this.refreshNecessario();
    if (!refreshNecessario) {
      return null;
    }

    const card = this.card();
    const tipo = card?.tipo;

    if (tipo === DashboardCardTipo.GRAFICO_PIZZA) {
      return this.buildOptionsPie();
    }

    const type =
      tipo === DashboardCardTipo.GRAFICO_COLUNA
        ? 'bar'
        : DashboardCardTipo.GRAFICO_LINHA
        ? 'line'
        : 'pie';

    return {
      chart: {
        type,
        height: '97%',
        width: '97%',
        toolbar: {
          show: false,
        },
      },
      // colors: colors,
      labels: this.getLabels(),
      noData: { text: 'Sem dados' },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        x: {
          formatter:
            type === 'bar'
              ? (val, opts) => {
                  return (
                    this.labelDadoAdicional().get(val.toString()) ||
                    val.toString()
                  );
                }
              : undefined,
        },
      },
      xaxis: {
        labels: {
          show: true,
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => {
            return Number(val).toLocaleString('pt-BR', {
              notation: 'compact',
              compactDisplay: 'short',
            });
          },
        },
      },
      responsive: [
        {
          // breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };
  }

  private buildOptionsPie(): Partial<ApexOptions> | null {
    return {
      chart: {
        width: '100%',
        height: '100%',
        type: 'pie',
      },
      // colors: colors,
      labels: this.getLabels(),
      noData: { text: 'Sem dados' },
      tooltip: {
        y: {
          title: {
            formatter: (seriesName) => {
              return this.labelDadoAdicional().get(seriesName) || seriesName;
            },
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => {
            return Number(val).toLocaleString('pt-BR', {
              notation: 'compact',
              compactDisplay: 'short',
            });
          },
        },
      },
    };
  }

  private buildSeries(): ApexAxisChartSeries | ApexNonAxisChartSeries | null {
    const refreshNecessario = this.refreshNecessario();
    if (!refreshNecessario) {
      return null;
    }

    const tipo = this.card()?.tipo;

    if (tipo === DashboardCardTipo.GRAFICO_PIZZA) {
      return this.buildSeriesNonAxis();
    }

    return this.buildSeriesAxis();
  }

  private buildSeriesAxis(): ApexAxisChartSeries {
    const series: ApexAxisChartSeries = [];
    const card = this.card();
    const colunas = this.dmController().colunas();
    const itens = this.dmController().conteudoVisivelDados() as DataManagerProxy<DashboardDadoCardItemModel>[];

    if (card && itens) {
      for (const valor of card.valores) {
        const coluna = colunas.find((col) => col.id === valor);
        if (!coluna) {
          continue;
        }

        const serie = {
          name: coluna?.descricao || valor,
          data: [] as number[],
        };
        for (const item of itens) {
          serie.data.push(item.get(coluna));
        }
        series.push(serie);
      }
    }

    return series;
  }

  private buildSeriesNonAxis(): ApexNonAxisChartSeries {
    const series: ApexNonAxisChartSeries = [];
    const card = this.card();
    const dados = card?.dados!;

    if (dados) {
      const valor = card!.valores[0];
      if (valor) {
        for (const item of dados!.itens) {
          series.push(item[valor]);
        }
      }
    }

    return series;
  }

  private getLabels() {
    const dados = this.dmController().conteudoVisivelDados() as DataManagerProxy<DashboardDadoCardItemModel>[];

    const card = this.card();
    const colunas = this.dmController().colunas();

    const dimensao = card?.dimensoes[0];
    const colunaDimensao = colunas.find(
      (col) => col.id === dimensao?.id
    ) as DataManagerColunaGrupo;
    if (!colunaDimensao) {
      return [];
    }

    const labels = dados.reduce<string[]>((prev, curr) => {
      const label = curr.get(colunaDimensao);

      if (colunaDimensao.tipoDado === CriterioTipoDado.data) {
        prev.push(label.substr(4, 2) + '/' + label.substr(0, 4));
      } else {
        prev.push(label!);
      }
      return prev;
    }, []);

    return labels;
  }
}
